// Shared puzzle / tournament links let a visitor play as a temporary guest without
// registering first. The guest is limited to that link: any other screen asks them
// to register (and then accept the terms) before continuing.

const GUEST_LINK_PASS_STORAGE_KEY = "xima_guest_link_pass";
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const GUEST_USERNAME_PREFIX = "invitadoo";
const GUEST_EMAIL_DOMAIN = "guest.xima.local";

export const GUEST_LINK_KINDS = Object.freeze({
  puzzle: "p",
  tournament: "t",
  independent: "i",
});

const VALID_KINDS = new Set(Object.values(GUEST_LINK_KINDS));

export const SHORT_LINK_PATH_PATTERN = /^\/([pti])\/([a-f0-9]{24})\/?$/i;

export const isValidGuestLinkId = (value) => OBJECT_ID_PATTERN.test(String(value || "").trim());

export const normalizeGuestLink = (kind, id) => {
  const normalizedKind = String(kind || "").trim().toLowerCase();
  const normalizedId = String(id || "").trim().toLowerCase();
  if (!VALID_KINDS.has(normalizedKind) || !isValidGuestLinkId(normalizedId)) return null;
  return { kind: normalizedKind, id: normalizedId };
};

export const isGuestSessionUser = (userValue) => {
  if (userValue?.isGuest === true) return true;
  return [userValue?.loginIdentifier, userValue?.email, userValue?.username, userValue?.sub].some(
    (value) => {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return false;
      return (
        normalized.startsWith(GUEST_USERNAME_PREFIX) ||
        normalized.endsWith(`@${GUEST_EMAIL_DOMAIN}`)
      );
    }
  );
};

const resolveUserKey = (userValue) =>
  String(userValue?.userId || userValue?.id || userValue?._id || "")
    .trim()
    .toLowerCase();

const getStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
};

let inMemoryPass = null;

export const readGuestLinkPass = () => {
  const storage = getStorage();
  if (storage) {
    try {
      const raw = storage.getItem(GUEST_LINK_PASS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const link = normalizeGuestLink(parsed?.kind, parsed?.id);
        if (link && parsed?.userKey) return { ...link, userKey: String(parsed.userKey) };
      }
    } catch (error) {
      // Fall back to the in-memory copy below.
    }
  }
  return inMemoryPass;
};

export const writeGuestLinkPass = (userValue, link) => {
  const normalized = normalizeGuestLink(link?.kind, link?.id);
  const userKey = resolveUserKey(userValue);
  if (!normalized || !userKey) return null;
  const pass = { ...normalized, userKey };
  inMemoryPass = pass;
  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(GUEST_LINK_PASS_STORAGE_KEY, JSON.stringify(pass));
    } catch (error) {
      // Private browsing: the in-memory pass keeps this visit working.
    }
  }
  return pass;
};

export const clearGuestLinkPass = () => {
  inMemoryPass = null;
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(GUEST_LINK_PASS_STORAGE_KEY);
  } catch (error) {
    // ignore
  }
};

// The pass only counts for the guest account that was created from the link.
export const getActiveGuestLinkPass = (userValue) => {
  if (!userValue || !isGuestSessionUser(userValue)) return null;
  const pass = readGuestLinkPass();
  if (!pass) return null;
  return pass.userKey === resolveUserKey(userValue) ? pass : null;
};

const splitRoute = (route) => {
  const raw = String(route || "").trim();
  const queryIndex = raw.indexOf("?");
  const pathname = (queryIndex >= 0 ? raw.slice(0, queryIndex) : raw).replace(/\/+$/, "") || "/";
  const search = queryIndex >= 0 ? raw.slice(queryIndex) : "";
  return { pathname, params: new URLSearchParams(search) };
};

const resolveStateObject = (state) =>
  state && typeof state === "object" && !Array.isArray(state) ? state : {};

// Which shared link (if any) this route represents. Only real URL data counts here,
// because a link pasted in a browser never carries router state.
export const resolveGuestLinkFromRoute = (pathname, search = "") => {
  const { pathname: path, params } = splitRoute(`${pathname || ""}${search || ""}`);
  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "game") return null;

  if (parts.length === 2) {
    if (params.get("puzzle") === "1") return normalizeGuestLink(GUEST_LINK_KINDS.puzzle, parts[1]);
    return null;
  }
  if (parts.length !== 1) return null;

  const shareType = String(params.get("shareType") || "").trim().toLowerCase();
  if (shareType === "puzzle") {
    return normalizeGuestLink(GUEST_LINK_KINDS.puzzle, params.get("puzzleId"));
  }
  if (shareType === "tournament" || shareType === "independent") {
    return normalizeGuestLink(
      shareType === "independent" ? GUEST_LINK_KINDS.independent : GUEST_LINK_KINDS.tournament,
      params.get("tournamentId")
    );
  }
  const waitingId = params.get("openTournamentWaitingId");
  if (waitingId) return normalizeGuestLink(GUEST_LINK_KINDS.tournament, waitingId);
  return null;
};

// A match room address copied from the browser bar (juegoxima.com/#/game/ID?autoplay=1).
// Only used for visitors without a session, so they enter that room as a guest.
export const resolveDirectRoomLinkFromRoute = (pathname, search = "") => {
  const shared = resolveGuestLinkFromRoute(pathname, search);
  if (shared) return shared;
  const { pathname: path } = splitRoute(`${pathname || ""}${search || ""}`);
  const parts = path.split("/").filter(Boolean);
  if (parts.length !== 2 || parts[0] !== "game") return null;
  return normalizeGuestLink(GUEST_LINK_KINDS.tournament, decodeURIComponent(parts[1]));
};

// Is this screen part of the link the guest was invited to?
export const isRouteInGuestLinkScope = (pass, location) => {
  if (!pass) return false;
  const { pathname: path, params } = splitRoute(
    `${location?.pathname || ""}${location?.search || ""}`
  );
  const state = resolveStateObject(location?.state);
  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "game") return false;
  const sameId = (value) => String(value || "").trim().toLowerCase() === pass.id;

  if (parts.length === 2) {
    if (sameId(decodeURIComponent(parts[1]))) return true;
    // Reconnecting to the running tournament match after a network drop.
    return pass.kind !== GUEST_LINK_KINDS.puzzle && params.get("manualReconnect") === "1";
  }
  if (parts.length !== 1) return false;

  const linkInRoute = resolveGuestLinkFromRoute(path, `?${params.toString()}`);
  if (linkInRoute && linkInRoute.id === pass.id) return true;

  if (pass.kind === GUEST_LINK_KINDS.puzzle) {
    // "Reintentar" reopens the same puzzle through the home screen.
    return sameId(state.openPuzzleId) || sameId(state.openRetryPuzzleFromId);
  }
  return sameId(state.openTournamentWaitingId);
};

export const buildGuestLinkTarget = (link) => {
  const normalized = normalizeGuestLink(link?.kind, link?.id);
  if (!normalized) return "";
  if (normalized.kind === GUEST_LINK_KINDS.puzzle) {
    return `/game/${normalized.id}?autoplay=1&puzzle=1`;
  }
  // Open the waiting room directly (not through ?shareType=...), so its back button
  // leaves the room instead of reopening the shared link.
  const params = new URLSearchParams({
    quickView: normalized.kind === GUEST_LINK_KINDS.independent ? "independientes" : "torneos",
    openTournamentWaitingId: normalized.id,
    openTournamentWaitingRequestKey: `link-${normalized.id}-${Date.now()}`,
  });
  return `/game?${params.toString()}`;
};

export const buildGuestLinkEntryPath = (link, target = "") => {
  const normalized = normalizeGuestLink(link?.kind, link?.id);
  if (!normalized) return "/";
  const base = `/entrar/${normalized.kind}/${normalized.id}`;
  return target ? `${base}?to=${encodeURIComponent(target)}` : base;
};

// Only accept a return target that is itself the same shared link.
export const resolveSafeGuestLinkTarget = (link, rawTarget) => {
  const target = String(rawTarget || "").trim();
  if (!target || !target.startsWith("/game") || target.startsWith("//")) {
    return buildGuestLinkTarget(link);
  }
  const { pathname, params } = splitRoute(target);
  const targetParts = pathname.split("/").filter(Boolean);
  if (
    link?.kind !== GUEST_LINK_KINDS.puzzle &&
    targetParts.length === 2 &&
    String(decodeURIComponent(targetParts[1])).toLowerCase() === link?.id
  ) {
    // The same match room the visitor opened.
    return target;
  }
  const linkInTarget = resolveGuestLinkFromRoute(pathname, `?${params.toString()}`);
  if (!linkInTarget || linkInTarget.id !== link?.id) return buildGuestLinkTarget(link);
  if (params.get("shareType")) return buildGuestLinkTarget(link);
  return target;
};

export const buildShortShareUrl = (kind, id) => {
  const normalized = normalizeGuestLink(kind, id);
  if (!normalized) return "";
  const origin =
    typeof window !== "undefined" ? String(window.location.origin || "").trim() : "";
  return `${origin}/${normalized.kind}/${normalized.id}`;
};
